class UpsellItems extends HTMLElement {
  connectedCallback() {
    this.requiredBodyClass = this.dataset.requireBodyClass || null;
    this.initialized = false;

    if (this.requiredBodyClass) {
      this.syncAbTestVisibility = this.syncAbTestVisibility.bind(this);
      this.syncAbTestVisibility();

      if (!this.initialized) {
        this.abTestObserver = new MutationObserver(this.syncAbTestVisibility);
        this.abTestObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return;
      }
    }

    this.setup();
  }

  disconnectedCallback() {
    this.abTestObserver?.disconnect();

    if (this.onVariantChange) {
      document.removeEventListener('variant:change', this.onVariantChange);
    }
  }

  syncAbTestVisibility() {
    const shouldShow = document.body.classList.contains(this.requiredBodyClass);

    if (shouldShow) {
      this.removeAttribute('hidden');

      if (!this.initialized) {
        this.setup();
      }
    } else {
      this.setAttribute('hidden', '');
    }
  }

  setup() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.checkboxes = Array.from(this.querySelectorAll('[data-upsell-checkbox]'));
    this.totalEl = this.querySelector('[data-upsell-total]');
    this.button = this.querySelector('[data-upsell-add-button]');
    this.buttonTemplate = this.button?.dataset.buttonTemplate || 'Add all __COUNT__ to cart';
    this.syncedCards = Array.from(this.querySelectorAll('[data-upsell-sync]'));
    this.formId = this.dataset.formId || null;
    this.productForm = null;

    this.currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
      style: 'currency',
      currency: Shopify.currency.active
    });

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.updateTotals());
    });

    this.button?.addEventListener('click', () => this.addToCart());

    if (this.syncedCards.length > 0) {
      this.onVariantChange = this.onVariantChange.bind(this);
      document.addEventListener('variant:change', this.onVariantChange);
    }

    this.updateTotals();
  }

  getProductForm() {
    if (!this.productForm && this.formId) {
      this.productForm = document.getElementById(this.formId);
    }

    return this.productForm;
  }

  onVariantChange(event) {
    const { variant } = event.detail || {};

    if (!variant) {
      return;
    }

    const productForm = this.getProductForm();

    if (productForm && event.target !== productForm) {
      return;
    }

    this.syncedCards.forEach((card) => this.syncCardWithVariant(card, variant));
    this.updateTotals();
  }

  syncCardWithVariant(card, variant) {
    const checkbox = card.querySelector('[data-upsell-checkbox]');
    const imageEl = card.querySelector('[data-upsell-image]');

    if (checkbox) {
      checkbox.dataset.variantId = String(variant.id);
      checkbox.dataset.price = String(variant.price);
      checkbox.disabled = !variant.available;

      if (!variant.available) {
        checkbox.checked = false;
      }
    }

    card.classList.toggle('upsell-items__card--unavailable', !variant.available);

    this.updateCardPrice(card, variant);

    if (imageEl) {
      const imageUrl = this.getVariantImageUrl(variant);

      if (imageUrl) {
        imageEl.src = this.getSizedImageUrl(imageUrl, 120);
        imageEl.srcset = [120, 240, 360]
          .map((width) => `${this.getSizedImageUrl(imageUrl, width)} ${width}w`)
          .join(', ');
      }
    }
  }

  updateCardPrice(card, variant) {
    const priceEl = card.querySelector('[data-upsell-price]');
    const compareEl = card.querySelector('[data-upsell-compare-price]');
    const isOnSale = variant.compare_at_price > variant.price;

    if (priceEl) {
      priceEl.textContent = this.currencyFormatter.format(variant.price / 100);
      priceEl.classList.toggle('upsell-items__card-price--sale', isOnSale);
    }

    if (compareEl) {
      if (isOnSale) {
        compareEl.textContent = this.currencyFormatter.format(variant.compare_at_price / 100);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  getVariantImageUrl(variant) {
    const media = variant.featured_media || variant.featured_image;

    if (!media) {
      return null;
    }

    if (media.preview_image?.src) {
      return media.preview_image.src;
    }

    if (typeof media === 'string') {
      return media;
    }

    return media.src || null;
  }

  getSizedImageUrl(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }

  getSelectedCheckboxes() {
    return this.checkboxes.filter((checkbox) => checkbox.checked && !checkbox.disabled);
  }

  updateTotals() {
    const selected = this.getSelectedCheckboxes();
    const totalCents = selected.reduce((sum, checkbox) => sum + parseInt(checkbox.dataset.price, 10), 0);

    if (this.totalEl) {
      this.totalEl.textContent = this.currencyFormatter.format(totalCents / 100);
    }

    if (this.button) {
      this.button.textContent = this.buttonTemplate.replace('__COUNT__', String(selected.length));
      this.button.disabled = selected.length === 0;
    }
  }

  async addToCart() {
    const items = this.getSelectedCheckboxes().map((checkbox) => ({
      id: parseInt(checkbox.dataset.variantId, 10),
      quantity: 1
    }));

    if (!items.length || !this.button) {
      return;
    }

    this.button.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname
        })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || 'Could not add items to cart');
      }

      if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent,
            items: responseJson.items || [responseJson]
          }
        })
      );
    } catch (error) {
      console.error('Upsell items add to cart error:', error);
      alert(error.message);
    } finally {
      this.button.removeAttribute('aria-busy');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
}

if (!window.customElements.get('upsell-items')) {
  window.customElements.define('upsell-items', UpsellItems);
}
